/**
 * Accounting Module - Main Container
 *
 * Central component that manages routing between all accounting subsections.
 * Supports both internal tab navigation and external sidebar navigation.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AccountingDashboard,
  CustomersPage,
  InvoicesPage,
  MovementsPage,
  BankingPage,
  ChartOfAccountsPage,
  DueDatesPage,
  ReportsPage,
} from './pages';

// =============================================================================
// TYPES
// =============================================================================

export type AccountingSection =
  | 'dashboard'
  | 'customers'
  | 'invoices'
  | 'movements'
  | 'banking'
  | 'chartofaccounts'
  | 'duedates'
  | 'reports';

interface AccountingModuleProps {
  /**
   * Initial section to display
   */
  initialSection?: AccountingSection;

  /**
   * Callback when section changes (for external routing)
   */
  onSectionChange?: (section: AccountingSection) => void;

  /**
   * External navigation handler (for app-level navigation)
   */
  onNavigateExternal?: (path: string) => void;

  /**
   * Hide internal tab navigation (when using sidebar navigation)
   */
  hideTabNavigation?: boolean;
}

// =============================================================================
// SECTION CONFIGURATION
// =============================================================================

interface SectionConfig {
  id: AccountingSection;
  label: string;
  icon: string;
}

const SECTIONS: SectionConfig[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
  },
  {
    id: 'customers',
    label: 'Clienti',
    icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
  },
  {
    id: 'invoices',
    label: 'Fatture',
    icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  },
  {
    id: 'movements',
    label: 'Prima Nota',
    icon: 'M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4',
  },
  {
    id: 'banking',
    label: 'Banca',
    icon: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z',
  },
  {
    id: 'chartofaccounts',
    label: 'Piano Conti',
    icon: 'M4 6h16M4 10h16M4 14h16M4 18h16',
  },
  {
    id: 'duedates',
    label: 'Scadenze',
    icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
  },
  {
    id: 'reports',
    label: 'Report',
    icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  },
];

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function AccountingModule({
  initialSection = 'dashboard',
  onSectionChange,
  onNavigateExternal,
  hideTabNavigation = true, // Default to hiding tabs (sidebar navigation is preferred)
}: AccountingModuleProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlSection = searchParams.get('section') as AccountingSection | null;

  // Use URL section if available, otherwise use state
  const [currentSection, setCurrentSection] = useState<AccountingSection>(
    urlSection || initialSection
  );

  // Sync with URL changes (from sidebar navigation)
  useEffect(() => {
    if (urlSection && SECTIONS.some((s) => s.id === urlSection)) {
      setCurrentSection(urlSection);
      onSectionChange?.(urlSection);
    }
  }, [urlSection, onSectionChange]);

  const handleNavigate = useCallback(
    (path: string) => {
      // Check if it's an internal accounting section
      const section = path.split('/')[0] as AccountingSection;
      if (SECTIONS.some((s) => s.id === section)) {
        setCurrentSection(section);
        setSearchParams({ section });
        onSectionChange?.(section);
      } else if (onNavigateExternal) {
        onNavigateExternal(path);
      }
    },
    [onSectionChange, onNavigateExternal, setSearchParams]
  );

  const handleBackToDashboard = useCallback(() => {
    setCurrentSection('dashboard');
    setSearchParams({ section: 'dashboard' });
    onSectionChange?.('dashboard');
  }, [onSectionChange, setSearchParams]);

  // Render current section
  const renderSection = () => {
    switch (currentSection) {
      case 'dashboard':
        return <AccountingDashboard onNavigate={handleNavigate} />;
      case 'customers':
        return <CustomersPage onNavigate={handleNavigate} onBack={handleBackToDashboard} />;
      case 'invoices':
        return <InvoicesPage onNavigate={handleNavigate} onBack={handleBackToDashboard} />;
      case 'movements':
        return <MovementsPage onNavigate={handleNavigate} onBack={handleBackToDashboard} />;
      case 'banking':
        return <BankingPage onNavigate={handleNavigate} onBack={handleBackToDashboard} />;
      case 'chartofaccounts':
        return <ChartOfAccountsPage onNavigate={handleNavigate} onBack={handleBackToDashboard} />;
      case 'duedates':
        return <DueDatesPage onNavigate={handleNavigate} onBack={handleBackToDashboard} />;
      case 'reports':
        return <ReportsPage onNavigate={handleNavigate} onBack={handleBackToDashboard} />;
      default:
        return <AccountingDashboard onNavigate={handleNavigate} />;
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Tab Navigation - Only shown when hideTabNavigation is false */}
      {!hideTabNavigation && (
        <div
          style={{
            display: 'flex',
            gap: '0.25rem',
            padding: '0.5rem 1rem',
            borderBottom: '1px solid var(--border, #e5e7eb)',
            background: 'var(--bg, white)',
            overflowX: 'auto',
          }}
        >
          {SECTIONS.map((section) => (
            <button
              key={section.id}
              onClick={() => {
                setCurrentSection(section.id);
                setSearchParams({ section: section.id });
                onSectionChange?.(section.id);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
                padding: '0.5rem 0.75rem',
                background: currentSection === section.id ? 'var(--brand, #3b82f6)' : 'transparent',
                color: currentSection === section.id ? 'white' : 'var(--text-secondary, #6b7280)',
                border: 'none',
                borderRadius: '0.5rem',
                fontSize: '0.8125rem',
                fontWeight: 500,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'background 0.15s, color 0.15s',
              }}
            >
              <svg
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d={section.icon}
                />
              </svg>
              {section.label}
            </button>
          ))}
        </div>
      )}

      {/* Content Area */}
      <div style={{ flex: 1, overflow: 'auto', background: 'var(--bg-secondary, #f9fafb)' }}>
        {renderSection()}
      </div>
    </div>
  );
}

export type { AccountingModuleProps };
